import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../models/ApiResponse";
import { SavedFilter } from "../models/ConsultantFilter";
import { CustomerSavedFilter } from "../models/CustomerFilter";
import { JobSavedFilter } from "../models/JobFilter";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";
import { useAuthStore } from "../stores/auth.store";

/** Free-form, frontend-owned preferences stored on the user's profile. */
export interface UserPreferences {
	consultantFilters?: SavedFilter[];
	customerFilters?: CustomerSavedFilter[];
	jobFilters?: JobSavedFilter[];
	/** Column-visibility overrides for the consultant/customer/job list tables (see useListColumns). */
	consultantListColumns?: Record<string, boolean>;
	customerListColumns?: Record<string, boolean>;
	jobListColumns?: Record<string, boolean>;
	/** Column-width overrides (relative weights) for the consultant/customer/job list tables. */
	consultantListColumnWidths?: Record<string, number>;
	customerListColumnWidths?: Record<string, number>;
	jobListColumnWidths?: Record<string, number>;
	/**
	 * User-defined left-to-right column order for the consultant/customer/job list
	 * tables: the full ordered key list, stored only while it deviates from the
	 * coded order (an empty array means "no override"). See ListColumns/columnOrder.
	 */
	consultantListColumnOrder?: string[];
	customerListColumnOrder?: string[];
	jobListColumnOrder?: string[];
	/**
	 * Per-user opt-in/out of beta features (see hooks/betaFeatures). A key that is
	 * absent means "no choice made" and falls back to the feature's own default
	 * gate; an explicit `false` turns a feature off even where it would otherwise
	 * be available.
	 */
	betaFeatures?: Record<string, boolean>;
	[key: string]: any;
}

export interface CurrentUser {
	id: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	roles?: string[];
	profile?: {
		organisation?: { id: string };
		preferences?: UserPreferences;
	};
}

const currentUserKey = (userId?: string) => ["current-user", userId];

/**
 * The logged-in user, including `profile.preferences`. Served by the
 * self-service `/me` endpoint, which needs only authentication (no admin
 * account.organisation.get scope).
 */
export function useCurrentUser() {
	const authStore = useAuthStore();

	return useQuery(
		currentUserKey(authStore.userId),
		() =>
			apiStore
				.get<CurrentUser>({
					path: `/me`,
					isAuthRequired: true,
					query: {},
				})
				.then((d) => d.data),
		{
			enabled: !!authStore.userId,
			// Preferences change rarely: serve cached for 30 min and keep in cache (and
			// persisted to localStorage) for a day so they survive reloads. Preference
			// mutations invalidate this key to refetch the server's merged result.
			staleTime: 30 * 60 * 1000,
			cacheTime: 24 * 60 * 60 * 1000,
		}
	);
}

/**
 * Update the caller's own preferences (merged server-side into
 * profile.preferences). Optimistic so filter edits feel instant; no success
 * toast since saving filters is a frequent, low-ceremony action.
 */
export function useUpdateMyPreferences() {
	const authStore = useAuthStore();
	const queryClient = useQueryClient();
	const key = currentUserKey(authStore.userId);

	return useMutation<any, ApiError, UserPreferences, { prev?: CurrentUser }>(
		(preferences) =>
			apiStore
				.put({
					path: `/me/preferences`,
					isAuthRequired: true,
					body: { preferences },
				})
				.then((d) => d.data),
		{
			onMutate: async (preferences) => {
				await queryClient.cancelQueries(key);
				const prev = queryClient.getQueryData<CurrentUser>(key);
				queryClient.setQueryData<CurrentUser>(key, (old) =>
					old
						? {
								...old,
								profile: {
									...old.profile,
									preferences: { ...old.profile?.preferences, ...preferences },
								},
						  }
						: old
				);
				return { prev };
			},
			onError: (_err, _vars, context) => {
				if (context?.prev) queryClient.setQueryData(key, context.prev);
				toast.errorGeneric();
			},
			// Re-fetch the server's merged preferences after every update (success or
			// failure) so the cache — and the persisted localStorage copy — reflect the
			// stored truth rather than only the optimistic patch.
			onSettled: () => {
				queryClient.invalidateQueries(key);
			},
		}
	);
}
